import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlane,
  faClock,
  faGlobe,
  faMedal,
  faTrophy,
  faLock,
  faCheckCircle,
  faSpinner,
  faStar,
  faFire
} from '@fortawesome/free-solid-svg-icons';
import './CareerCardFramework.css';
import './BattlePass.css';

const BattlePass = () => {
  const [battlePass, setBattlePass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);

  const iconMap = {
    faPlane,
    faClock,
    faGlobe,
    faMedal,
    faTrophy
  };

  useEffect(() => {
    fetchBattlePass();
  }, []);

  const fetchBattlePass = async () => {
    try {
      const response = await api.get('/career/battle-pass/current');

      setBattlePass(response.data.battlePass);
      setCompletionPercentage(response.data.completionPercentage || 0);
      setTotalPoints(response.data.totalPoints || 0);
    } catch (error) {
      console.error('Error fetching battle pass:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChallengeIcon = (iconName) => {
    return iconMap[iconName] || faPlane;
  };

  const getRarityColor = (points) => {
    if (points >= 250) return 'legendary';
    if (points >= 200) return 'epic';
    if (points >= 150) return 'rare';
    return 'common';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long'
    });
  };

  const getDaysRemaining = () => {
    if (!battlePass) return 0;
    const now = new Date();
    const end = new Date(battlePass.endDate);
    const diff = end - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="battle-pass-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>Cargando desafíos mensuales...</p>
      </div>
    );
  }

  if (!battlePass) {
    return (
      <div className="battle-pass-error">
        <p>No hay desafíos activos en este momento</p>
      </div>
    );
  }

  return (
    <div className="battle-pass-container">
      <div className="battle-pass-header">
        <div className="battle-pass-title-section">
          <FontAwesomeIcon icon={faFire} className="fire-icon" />
          <h1>Desafíos Mensuales</h1>
          <span className="battle-pass-month">
            {new Date(battlePass.startDate).toLocaleDateString('es-ES', {
              month: 'long',
              year: 'numeric'
            }).toUpperCase()}
          </span>
        </div>

        <div className="career-stats-grid">
          <div className="career-stat-item">
            <span className="career-stat-value">{getDaysRemaining()}</span>
            <span className="career-stat-label">Días Restantes</span>
          </div>
          <div className="career-stat-item">
            <span className="career-stat-value">{completionPercentage}%</span>
            <span className="career-stat-label">Progreso Total</span>
          </div>
          <div className="career-stat-item">
            <span className="career-stat-value">
              <FontAwesomeIcon icon={faStar} /> {totalPoints}
            </span>
            <span className="career-stat-label">Puntos Ganados</span>
          </div>
        </div>
      </div>

      <div style={{ 
        background: 'var(--background-primary)',
        padding: '1.5rem',
        borderRadius: '1rem',
        marginBottom: '1.5rem',
        border: '1px solid var(--background-secondary)'
      }}>
        <div style={{ position: 'relative' }}>
          <div className="career-progress-bar" style={{ height: '30px' }}>
            <div className="career-progress-fill" style={{ width: `${Math.max(completionPercentage, 10)}%`, position: 'relative' }} />
          </div>
          <div style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            color: completionPercentage > 50 ? 'white' : 'var(--text-primary)',
            fontWeight: '600',
            fontSize: '0.9rem',
            textShadow: completionPercentage > 50 ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}>
            {completionPercentage}% Completado
          </div>
        </div>
      </div>

      <div className="career-cards-grid cols-3">
        {battlePass.challenges.map((challenge) => (
          <div
            key={challenge.id}
            className={`career-card ${
              challenge.completed ? 'completed' : ''
            } rarity-${getRarityColor(challenge.points)}`}
          >
            <div className="career-card-glow" />
            
            <div className="career-card-header">
              <div className={`career-card-icon ${
                challenge.completed ? 'completed' : ''
              }`}>
                <FontAwesomeIcon
                  icon={getChallengeIcon(challenge.icon)}
                />
                {challenge.completed && (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: 'var(--background-primary)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      style={{ color: '#10b981', fontSize: '1rem' }}
                    />
                  </span>
                )}
              </div>
              <div className="career-card-badge primary">
                <FontAwesomeIcon icon={faStar} />
                <span>{challenge.points}</span>
              </div>
            </div>

            <div className="career-card-content">
              <h3 className="career-card-title">{challenge.name}</h3>
              <p className="career-card-description">{challenge.description}</p>
            </div>

            <div className="career-progress-wrapper">
              <div className="career-progress-bar">
                <div
                  className="career-progress-fill"
                  style={{
                    width: `${Math.min(
                      100,
                      (challenge.currentValue / challenge.targetValue) * 100
                    )}%`
                  }}
                />
              </div>
              <div className="career-progress-text">
                <span className="career-progress-current">
                  {challenge.currentValue}
                </span>
                <span className="career-progress-target">
                  / {challenge.targetValue}
                </span>
              </div>
            </div>

            {challenge.completed && (
              <div className="career-card-footer">
                <div className="career-card-date">
                  <FontAwesomeIcon icon={faTrophy} />
                  <span> Completado el {formatDate(challenge.completedAt)}</span>
                </div>
              </div>
            )}

          </div>
        ))}
      </div>

      {completionPercentage === 100 && (
        <div className="battle-pass-complete">
          <FontAwesomeIcon icon={faTrophy} size="3x" />
          <h2>¡Desafíos Completados!</h2>
          <p>Has completado todos los desafíos de este mes</p>
        </div>
      )}
    </div>
  );
};

export default BattlePass;